import {
  NFE_PAYMENT_CODES,
  PAYMENT_KINDS,
  PAYMENT_KIND_DEFAULT_TPAG,
  PAYMENT_KIND_LABEL,
  paymentMethodInputSchema,
  paymentMethodUpdateSchema,
  type PaymentKind,
  type PaymentMethod,
} from "@cafegestor/shared";
import { useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { issuesToErrors, useCadastroList, useForm, useListState, useSave } from "./lib";
import { Checkbox, ListToolbar, Pager, SaveError, StatusBadge } from "./widgets";

export function PaymentMethodsPage() {
  const canEdit = useCan()("cadastros.precos.gerenciar");
  const ls = useListState();
  const [editing, setEditing] = useState<PaymentMethod | "new" | null>(null);
  const list = useCadastroList<PaymentMethod>("formas-pagamento", ls);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Formas de pagamento"
        description="Usadas nas vendas e nos recebimentos. O código tPag vai para a nota fiscal."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Nova forma
            </Button>
          )
        }
      />
      {editing && (
        <PaymentMethodEditor
          key={editing === "new" ? "new" : editing.id}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <ListToolbar
          q={ls.q}
          onQ={ls.setQ}
          status={ls.status}
          onStatus={ls.setStatus}
          placeholder="Buscar por nome"
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Nome", "Tipo", "tPag", "Parcelas", "Contas a receber", "Situação", ""]}>
              {list.data?.items.map((m) => (
                <tr key={m.id}>
                  <td className="px-3 py-2 font-medium">{m.name}</td>
                  <td className="px-3 py-2">{PAYMENT_KIND_LABEL[m.kind]}</td>
                  <td className="px-3 py-2 font-mono">{m.nfePaymentCode}</td>
                  <td className="px-3 py-2">
                    {m.installments}x
                    {m.installments > 1 || m.firstDueDays > 0
                      ? ` · 1ª em ${m.firstDueDays} dias${m.installments > 1 ? `, depois a cada ${m.intervalDays}` : ""}`
                      : ""}
                  </td>
                  <td className="px-3 py-2">
                    {m.generatesReceivable ? (
                      <Badge tone="warn">Gera</Badge>
                    ) : (
                      <Badge>À vista</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={m.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(m)}>
                        Editar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={ls.offset} onOffset={ls.setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function PaymentMethodEditor({
  item,
  onClose,
}: {
  item: PaymentMethod | null;
  onClose: () => void;
}) {
  const f = useForm({
    name: item?.name ?? "",
    kind: (item?.kind ?? "dinheiro") as PaymentKind,
    nfePaymentCode: item?.nfePaymentCode ?? "01",
    installments: String(item?.installments ?? 1),
    firstDueDays: String(item?.firstDueDays ?? 0),
    intervalDays: String(item?.intervalDays ?? 30),
    generatesReceivable: item?.generatesReceivable ?? false,
    status: item?.status ?? "active",
  });
  const save = useSave<unknown, PaymentMethod>({
    resource: "formas-pagamento",
    id: item?.id ?? null,
    invalidate: ["formas-pagamento", "opcoes"],
    onSaved: onClose,
    successMessage: (_r, created) =>
      created
        ? "Forma de pagamento criada com sucesso."
        : "Forma de pagamento atualizada com sucesso.",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = item
      ? paymentMethodUpdateSchema.safeParse({ ...f.values, version: item.version })
      : paymentMethodInputSchema.safeParse(f.values);
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={item ? `Editar ${item.name}` : "Nova forma de pagamento"}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
        <Field label="Nome" error={f.errors["name"]} required>
          <Input {...f.text("name")} />
        </Field>
        <Field label="Tipo">
          <Select
            value={f.values.kind}
            onChange={(e) => {
              const kind = e.target.value as PaymentKind;
              f.setValues((v) => ({
                ...v,
                kind,
                nfePaymentCode: PAYMENT_KIND_DEFAULT_TPAG[kind],
                generatesReceivable:
                  kind === "boleto" || kind === "prazo" ? true : v.generatesReceivable,
              }));
            }}
          >
            {PAYMENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {PAYMENT_KIND_LABEL[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Código na NF-e (tPag)" error={f.errors["nfePaymentCode"]}>
          <Select {...f.text("nfePaymentCode")}>
            {Object.entries(NFE_PAYMENT_CODES).map(([code, label]) => (
              <option key={code} value={code}>
                {code} — {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Situação">
          <Select {...f.text("status")}>
            <option value="active">Ativa</option>
            <option value="inactive">Inativa</option>
          </Select>
        </Field>
        <Field label="Parcelas" error={f.errors["installments"]}>
          <Input type="number" min={1} max={60} {...f.text("installments")} />
        </Field>
        <Field label="1º vencimento (dias)" error={f.errors["firstDueDays"]}>
          <Input type="number" min={0} max={365} {...f.text("firstDueDays")} />
        </Field>
        <Field label="Intervalo entre parcelas (dias)" error={f.errors["intervalDays"]}>
          <Input type="number" min={0} max={365} {...f.text("intervalDays")} />
        </Field>
        <div className="flex items-end pb-2">
          <Checkbox label="Gera contas a receber" {...f.check("generatesReceivable")} />
        </div>
        {f.errors["generatesReceivable"] && (
          <p className="text-xs text-destructive sm:col-span-4">
            {f.errors["generatesReceivable"]}
          </p>
        )}
        <div className="flex gap-2 sm:col-span-4">
          <Button type="submit" disabled={save.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <div className="sm:col-span-4">
          <SaveError error={save.error} />
        </div>
      </form>
    </Card>
  );
}
